import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Download } from "lucide-react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useData } from "@/context/DataContext";
import { FinTable } from "@/components/FinTable";
import { KpiCard, Panel, PageHeader, ChartTip, StatusBadge } from "@/components/Widgets";
import { cut, downloadCsv, fmt, fmtCr, pct, sum, last } from "@/lib/api";
import { buildEntityRows, loanRows } from "@/pages/entityRows";

export default function Entities() {
  const { data, horizon, filters, setFilters } = useData();
  const { code } = useParams();
  useEffect(() => {
    if (code) setFilters((f) => ({ ...f, entity: code }));
  }, [code, setFilters]);
  const nav = useNavigate();
  useEffect(() => {
    if (!filters.entity && code) nav("/entities", { replace: true });
  }, [filters.entity]); // eslint-disable-line react-hooks/exhaustive-deps
  const [hideZero, setHideZero] = useState(true);
  const ents = data.model.entities;
  const e = ents.find((x) => x.code === filters.entity) || ents[0];
  const years = cut(data.model.years, horizon);
  const rows = buildEntityRows(e, horizon, hideZero, data.section_labels);
  const s = (k) => cut(e.series[k], horizon);
  const chart = years.map((y, i) => ({ y, income: s("income")[i], ebitda: s("ebitda")[i], ncf: s("ncf")[i] }));
  return (
    <div data-testid="entities-page">
      <PageHeader kicker={`${e.category} · sheet "${e.sheet}"`} title={e.name}>
        <Select value={e.code} onValueChange={(v) => { setFilters((f) => ({ ...f, entity: v })); nav(`/entities/${v}`); }}>
          <SelectTrigger data-testid="entity-select-dropdown" className="w-[260px] border-slate-700 bg-slate-900/70"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#131c2e]">
            {ents.map((x) => <SelectItem key={x.code} value={x.code} data-testid={`entity-option-${x.code}`}>{x.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>
      {!e.switch && (
        <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-2.5 text-sm text-amber-300" data-testid="entity-switch-off-note">
          This entity is switched off (switch = 0) in the NCF sheet, so it does not contribute to group cash flow.
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard testid="entity-kpi-revenue" label="Revenue (horizon)" value={fmtCr(sum(s("income")))} />
        <KpiCard testid="entity-kpi-ebitda" label="EBITDA margin" value={pct(sum(s("ebitda")) / sum(s("income")))} tone="blue" sub={fmtCr(sum(s("ebitda")))} />
        <KpiCard testid="entity-kpi-ncf" label="Net cash flow (horizon)" value={fmtCr(sum(s("ncf")))} tone={sum(s("ncf")) >= 0 ? "pos" : "neg"} />
        <KpiCard testid="entity-kpi-debt" label={`Loan closing FY ${last(years)}`} value={fmtCr(last(s("loan_closing")))} tone="neg" />
      </div>
      <Panel className="mt-6" title="Income, EBITDA & NCF" testid="entity-chart">
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={chart}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="y" tickLine={false} axisLine={false} />
            <YAxis tickLine={false} axisLine={false} tickFormatter={fmt} width={60} />
            <Tooltip content={<ChartTip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <ReferenceLine y={0} stroke="#475569" />
            <Bar dataKey="income" name="Income" fill="#3B82F6" radius={[3, 3, 0, 0]} />
            <Bar dataKey="ebitda" name="EBITDA" fill="#F59E0B" radius={[3, 3, 0, 0]} />
            <Line dataKey="ncf" name="Net cash flow" stroke="#10B981" strokeWidth={2.5} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </Panel>
      <Panel className="mt-6" title="Financial Projection" subtitle="Mapped from worksheet row labels · esc = 3-yearly escalation assumption"
        testid="entity-table-panel"
        actions={<>
          <label className="flex items-center gap-2 text-xs text-slate-400">
            <Switch checked={hideZero} onCheckedChange={setHideZero} data-testid="entity-hide-zero-toggle" /> Hide zero rows
          </label>
          <Button size="sm" variant="outline" data-testid="export-entity-csv-button" className="border-slate-700"
            onClick={() => downloadCsv(`${e.code}_projection.csv`, years, rows)}><Download className="mr-1.5 h-3.5 w-3.5" />CSV</Button>
        </>}>
        <FinTable years={years} rows={rows} testid="entity-fin-table" />
      </Panel>
      <EntityLoans e={e} years={years} />
    </div>
  );
}

const EntityLoans = ({ e, years }) => {
  const { horizon } = useData();
  if (!e.loans.length) return null;
  return (
    <Panel className="mt-6" title="Loan Details" subtitle={`${e.loans.length} facilities`} testid="entity-loans-panel">
      <div className="space-y-5">
        {e.loans.map((l, i) => (
          <div key={l.name + i} data-testid={`entity-loan-${i}`}>
            <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
              <span className="font-semibold text-slate-100">{l.name}</span>
              <span className="text-slate-400">{l.type || "—"} · {l.lender || "—"}</span>
              <StatusBadge ok={!l.prospective}>{l.prospective ? "Prospective" : "Existing"}</StatusBadge>
              {l.rate != null && <span className="num text-xs text-amber-400">@ {(l.rate * 100).toFixed(2)}%</span>}
            </div>
            <FinTable years={years} rows={loanRows(l, horizon)} testid={`entity-loan-table-${i}`} maxH="none" />
          </div>
        ))}
      </div>
    </Panel>
  );
};
